export type MatchType =
    | 'match-winner'
    | 'match-result'
    | 'both-teams-to-score'
    | 'total-goals-over-under'
    | 'draw-no-bet'
    | '1st-half-winner'
    | 'match-result-and-both-to-score'
    | 'money-line'
    | 'total-points-over-under'
    | '1st-half-money-line'
    | 'spread'
    | '1st-quarter-money-line'
    | 'point-spread'
    | 'handicap-games'
    | 'total-games-over-under';

export interface ItemType {
    name: string;
    id: MatchType;
    template: string;
    line: number | null;
}

export const getMarketTranslation = (
    getTranslation: (key: string, defaultText: string, params?: Record<string, string | number>) => string
): Record<MatchType, ItemType> => {
    const marketsArray: Record<MatchType, ItemType> = {
        'match-winner': {
            name: getTranslation('football.filter.marketName.match-winner', 'Match Winner'),
            id: 'match-winner',
            template: 'match-winner',
            line: null,
        },
        'match-result': {
            name: getTranslation('football.filter.marketName.match-result', 'Match Result'),
            id: 'match-result',
            template: 'match-result',
            line: null,
        },
        'both-teams-to-score': {
            name: getTranslation('football.filter.marketName.bot-teams-to-score', 'Both Teams To Score'),
            id: 'both-teams-to-score',
            template: 'both-teams-to-score',
            line: null,
        },
        'match-result-and-both-to-score': {
            name: getTranslation('football.filter.marketName.match-result-btts', 'Match Result BTTS'),
            id: 'match-result-and-both-to-score',
            template: 'match-result-and-both-to-score',
            line: null,
        },
        'total-goals-over-under': {
            name: getTranslation('football.filter.marketName.over-under-goals', 'Total Goals Over/Under'),
            id: 'total-goals-over-under',
            template: 'total-goals-over-under',
            line: null,
        },
        'draw-no-bet': {
            name: getTranslation('football.filter.marketName.draw-no-bet', 'Draw No Bet'),
            id: 'draw-no-bet',
            template: 'draw-no-bet',
            line: null,
        },
        '1st-half-winner': {
            name: getTranslation('football.filter.marketName.first-half-result', 'First Half Result'),
            id: '1st-half-winner',
            template: '1st-half-winner',
            line: null,
        },
        'money-line': {
            name: getTranslation('market.nba.moneyline', 'Money Line'),
            id: 'money-line',
            template: 'match-winner',
            line: null,
        },
        spread: {
            name: getTranslation('market.spread', 'Spread'),
            id: 'spread',
            template: 'spread',
            line: null,
        },
        'point-spread': {
            name: getTranslation('market.spread', 'Spread'),
            id: 'point-spread',
            template: 'point-spread',
            line: null,
        },
        'total-points-over-under': {
            name: getTranslation('market.nba.total.points', 'Total Points Over/Under'),
            id: 'total-points-over-under',
            template: 'total-points-over-under',
            line: null,
        },
        '1st-half-money-line': {
            name: getTranslation('basketball.filter.marketName.1st-half-moneyline', '1st Half Money Line'),
            id: '1st-half-money-line',
            template: '1st-half-money-line',
            line: null,
        },
        '1st-quarter-money-line': {
            name: getTranslation('basketball.filter.marketName.1st-quarter-moneyline', '1st Quarter Money Line'),
            id: '1st-quarter-money-line',
            template: '1st-quarter-money-line',
            line: null,
        },
        'handicap-games': {
            name: getTranslation('tennis.filter.marketName.game-handicap', 'Game Handicap'),
            id: 'handicap-games',
            template: 'handicap-games',
            line: null,
        },
        'total-games-over-under': {
            name: getTranslation('tennis.filter.marketName.total-games', 'Total Games'),
            id: 'total-games-over-under',
            template: 'total-games-over-under',
            line: null,
        },
    };

    return marketsArray;
};

const FOOTBALL_MARKETS: Array<MatchType> = [
    'match-winner',
    '1st-half-winner',
    'total-goals-over-under',
    'both-teams-to-score',
    'draw-no-bet',
    'match-result-and-both-to-score',
];

const BASKETBALL_MARKETS: Array<MatchType> = [
    'money-line',
    'point-spread',
    'total-points-over-under',
    '1st-half-money-line',
    '1st-quarter-money-line',
];

const AMERICAN_FOOTBALL_MARKETS: Array<MatchType> = ['money-line', 'point-spread', 'total-points-over-under'];

const TENNIS_MARKETS: Array<MatchType> = ['match-result', 'handicap-games', 'total-games-over-under'];

export const getMarkets = (
    getTranslation: (key: string, defaultText: string, params?: Record<string, string | number>) => string,
    sportId: string
): Array<ItemType> => {
    const array: Array<ItemType> = [];
    const marketsById = getMarketTranslation(getTranslation);

    if (sportId === 'football') {
        for (const matchTypeId of FOOTBALL_MARKETS) {
            const market = marketsById[matchTypeId];

            if (market !== undefined) {
                array.push(market);
            }
        }

        return array;
    }

    if (sportId === 'basketball') {
        for (const matchTypeId of BASKETBALL_MARKETS) {
            array.push(marketsById[matchTypeId]);
        }

        return array;
    }

    if (sportId === 'americanfootball') {
        for (const matchTypeId of AMERICAN_FOOTBALL_MARKETS) {
            const market = marketsById[matchTypeId];

            if (matchTypeId === 'point-spread') {
                array.push({
                    ...market,
                    template: 'two-way-handicap',
                });
            } else {
                array.push(market);
            }
        }

        return array;
    }

    if (sportId === 'tennis') {
        for (const matchTypeId of TENNIS_MARKETS) {
            const market = marketsById[matchTypeId];
            if (market === undefined) {
                continue;
            }

            if (matchTypeId === 'match-result') {
                // Label "Match Winner"; template stays `match-result` (feed).
                // `matchesMarketFilterTemplate` also accepts `match-winner` markets.
                array.push({
                    ...market,
                    name: getTranslation('tennis.filter.marketName.match-winner', 'Match Winner'),
                });
                continue;
            }

            array.push(market);
        }

        return array;
    }

    // Baseball uses fixed 6-pack columns (Run Line / Total / Money Line) — no market filters.
    return array;
};
